import "./Activity.scss";

import { Alert, Button, Flex, FlexItem, Text } from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { TeamsUsage, TeamsUser } from "@teams/types";
import {
  SubscriptionActivityDTO,
  SubscriptionActivityItemDTO,
  UserSubscriptionDTO,
} from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Column, HeaderGroup, useSortBy, useTable } from "react-table";

import { Identity } from "../../components/Identity";
import { PollyIcon } from "../../components/PollyIcon";
import { TeamsLoader } from "../../components/TeamsLoader";
import { isError } from "../../services/ChannelApi";
import { subscriptionApi } from "../../services/SubscriptionApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { getPlanLabel } from "../../types/subscriptionHelper";
import { getPlanLevelDisplayText } from "../../utils/paymentHelpers";
import { getPageContext } from "../../utils/teamsHelpers";
import { UserRoleBadge, UserSubscriptionRole } from "./UsersAndPermissionsForAccount";

type Props = {
  subscription: UserSubscriptionDTO;
  onUpgradeRequest: () => void;
};

export const Activity: React.FC<Props> = ({ subscription, onUpgradeRequest }) => {
  const { teamsContext } = useTeamsContext();
  const context = getPageContext();

  const [activityError, setActivityError] = useState<Error>();
  const [isActivityLoading, setActivityLoading] = useState<boolean>(false);
  const [activity, setActivity] = useState<SubscriptionActivityDTO>();

  const loadActivity = useCallback(async (teamsContext: app.Context, subscriptionId: string) => {
    setActivityError(undefined);
    setActivityLoading(true);
    const result = await subscriptionApi.getActivityHistory(teamsContext, subscriptionId);
    if (isError(result)) {
      setActivityError(result);
    } else {
      setActivity(result);
    }
    setActivityLoading(false);
  }, []);

  useEffect(() => {
    loadActivity(teamsContext, subscription.id);
  }, [teamsContext, subscription, loadActivity]);

  const tableData = useMemo<SubscriptionActivityItemDTO[]>(() => {
    return activity?.items || [];
  }, [activity]);

  const activityColumns = useMemo<Column<SubscriptionActivityItemDTO>[]>(
    () => [
      {
        Header: "Title",
        accessor: "title",
        width: "100%",
      },
      {
        Header: "Sender",
        accessor: "owner",
      },
      {
        Header: "Users",
        accessor: "users",
      },
      {
        Header: "Channel or Group Name",
        accessor: "channel",
      },
    ],
    []
  );

  const sortByColumn = useMemo(
    () => [
      {
        id: "owner",
        desc: true,
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, rows, prepareRow, headerGroups } = useTable(
    {
      columns: activityColumns,
      data: tableData,
      initialState: { sortBy: sortByColumn } as any,
    },
    useSortBy
  );

  const getCurrentCount = () => {
    if (!activity) {
      return 0;
    }
    const minimum = 10;
    if (context === "/agile" || activity.currentCount >= minimum) {
      return activity.currentCount;
    }
    return `${activity.currentCount} (${minimum} user minimum)`;
  };

  return (
    <>
      {activityError && (
        <Alert content={activityError.message} danger variables={{ error: true }} />
      )}

      <div>
        <TeamsLoader isLoading={isActivityLoading} loadingMessage="Loading activity">
          {subscription.planUsageType === "metered" && (
            <>
              <div>
                <Text content="Billing Period:" weight="bold" />
                <Text
                  content={`${moment(activity?.periodStart).format("LL")} - ${moment(
                    activity?.periodEnd
                  ).format("LL")}`}
                  as="div"
                />
              </div>

              <div className="account-settings-activity__field">
                <Text content="Users this period:" weight="bold" />
                <Text content={getCurrentCount()} as="div" />
              </div>
            </>
          )}

          {activity?.users && subscription.planLevel && (
            <Flex column gap="gap.medium">
              <Flex>
                <Flex column gap="gap.medium">
                  <Text content="Usage Activity" size="large" weight="semibold" />
                  <Text content="License holders and their usage" />
                </Flex>
                {getPlanLabel(
                  getPageContext() === "/agile" ? "agile-standup" : "msteams-standard",
                  subscription.planLevel,
                  subscription.scope
                ) === "Standard" && (
                  <FlexItem push>
                    <Button
                      primary
                      icon={<PollyIcon icon="plus-outlined" inheritColor />}
                      content="Increase Response Limit"
                      onClick={onUpgradeRequest}
                    />
                  </FlexItem>
                )}
              </Flex>
              <Flex column>
                {activity.users?.map(user => {
                  const isOwner = subscription.owners?.some(
                    owner => user.user.aadObjectId === owner.aadObjectId
                  );
                  return (
                    <UserProgressRow
                      role={isOwner ? "owner" : "user"}
                      usageData={user}
                      subscriptionPlanLevel={subscription.planLevel ?? 0}
                    />
                  );
                })}
              </Flex>
            </Flex>
          )}

          {activity?.items && activity.items.length > 0 && (
            <table className="account-settings-activity__table" {...getTableProps()}>
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps(
                          (
                            column as SortableColumn<SubscriptionActivityItemDTO>
                          ).getSortByToggleProps()
                        )}
                      >
                        <span className="survey-results__wrapped-cell">
                          {column.render("Header")}

                          {(column as SortableColumn<SubscriptionActivityItemDTO>).isSorted
                            ? (column as SortableColumn<SubscriptionActivityItemDTO>).isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="account-settings-activity__body" {...getTableBodyProps()}>
                {!activity?.items.length && (
                  <tr>
                    <td colSpan={4}>
                      <Text content="No Activity" align="center" size="large" weight="bold" />
                    </td>
                  </tr>
                )}

                {rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </TeamsLoader>
      </div>
    </>
  );
};

type SortableColumn<T extends object> = HeaderGroup<T> & {
  isSorted: boolean;
  isSortedDesc: boolean;
  getSortByToggleProps: () => any;
};
function getClassFromUsage(progress: number) {
  if (progress >= 51 && progress <= 75) {
    return "medium";
  } else if (progress > 75) {
    return "high";
  }
  return "low";
}
const UserProgressRow: React.FC<{
  usageData: { user: TeamsUser; usage: TeamsUsage };
  subscriptionPlanLevel: number;
  role: UserSubscriptionRole;
}> = ({ usageData, role, subscriptionPlanLevel }) => {
  const progressPercentage = useMemo(() => {
    return usageData.usage?.value ? (usageData.usage.value / subscriptionPlanLevel) * 100 : 0;
  }, [subscriptionPlanLevel, usageData.usage?.value]);

  const { user, usage } = usageData;
  return (
    <Flex className="usage-meter__user-row" key={user.aadObjectId}>
      <Flex vAlign="center" className="name-and-badge">
        <div className="usage-meter__user-row__identity">
          <Identity name={user.name} subname={user.upn} />
        </div>
        <UserRoleBadge role={role} user={user} badgeOnly={true} />
      </Flex>
      <Flex vAlign="center" className="meter-and-usage">
        <div className="meter-bar">
          <div
            className={`meter meter__${getClassFromUsage(progressPercentage)}`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className="usage-text">
          <Text
            weight="semibold"
            content={`${usage?.value || 0}/${getPlanLevelDisplayText(subscriptionPlanLevel)} `}
          />
          <Text content={`monthly responses`} />
        </div>
      </Flex>
    </Flex>
  );
};
